import React, { useEffect } from "react";
import { Phone, Mail, Sparkles, Heart, Smile, Leaf } from "lucide-react";
import SubpageHero from "@/components/SubpageHero";
import SubpageLayout from "@/components/SubpageLayout";
import SectionNumber from "@/components/SectionNumber";
import UpcomingEvents from "@/components/UpcomingEvents";
import PageGallery from "@/components/PageGallery";
import Embers from "@/components/Embers";
import { SITE } from "@/lib/data";
import useScrollReveal from "@/hooks/useScrollReveal";
import { BookingProcess } from "./FalusiVendegasztal";

const OFFERS = [
  { text: "Egyszerű, finom ételek elkészítése játékosan", Icon: Sparkles },
  { text: "Ismerkedés az alapanyagokkal: honnan jön az étel?", Icon: Leaf },
  { text: "Kézzel fogható élmények: gyúrás, keverés, kóstolás", Icon: Smile },
  { text: "Kapcsolódás az állatokhoz és a vidéki élethez", Icon: Heart },
  { text: "Sok nevetés, sikerélmény és közös alkotás", Icon: Sparkles },
];

export default function GyerekWorkshopok() {
  useScrollReveal();
  useEffect(() => {
    document.title = "Gyerek workshopok · Pakkaland";
  }, []);

  return (
    <SubpageLayout testId="page-gyerek-workshopok">
      <SubpageHero
        crumb="Gyerek workshopok"
        eyebrow="Pakkaland főző suli"
        title="Gyerek workshopok"
        tagline="Üdvözlünk a Pakkaland főző suliban! Játékos főzés gyerekeknek, gyerekekkel — szeretettel."
        image="/images/bx5lhx0h.jpg"
      />

      {/* Intro */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="01" />
        {/* Warm radial blob */}
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #FFA64D 0%, #D90429 55%, transparent 75%)",
          }}
        />
        <Embers count={8} variant="light" />

        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center relative">
          <div className="lg:col-span-7 reveal">
            <span className="eyebrow">Helyszín</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-[3.75rem] mt-6 leading-[1.02]">
              Falusi<br />
              <span className="italic text-pkred">vendégház.</span>
            </h2>

            {/* Decorative ember dot + line */}
            <div className="mt-6 flex items-center gap-3">
              <span className="block w-12 h-px bg-pkred" />
              <span className="block w-1.5 h-1.5 rounded-full bg-pkred shadow-[0_0_10px_rgba(217,4,41,0.7)]" />
            </div>

            <p className="mt-8 text-pkcharcoal/75 text-base sm:text-lg leading-relaxed">
              A falusi vendégház hangulatát idéző, meghitt környezetben várjuk
              a kis kuktákat, ahol minden a nyugalomról, az ízekről,
              élményekről szól. A Tisza közelsége, a tágas udvar és a
              barátságos állatok mind hozzájárulnak ahhoz, hogy a gyerekek
              kiszakadjanak a rohanó hétköznapokból, és valódi kapcsolatba
              kerüljenek a természettel és az ízekkel.
            </p>

            {/* Feature chips */}
            <div className="mt-8 flex flex-wrap gap-2">
              {["Tisza-part", "Tágas udvar", "Barátságos állatok", "Kemence", "Kis konyha"].map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 bg-white border border-pkash text-[0.7rem] tracking-[0.15em] uppercase text-pkcharcoal/75 font-semibold hover:border-pkred hover:text-pkred transition-colors cursor-default"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Image stack with polaroid + 2014 tag */}
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="relative aspect-[4/5] overflow-hidden bg-pkbeige">
                <img
                  src="/images/bx5lhx0h.jpg"
                  alt="Pakkaland kuktái"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/35 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm px-3 py-2 shadow-md">
                  <div className="text-[0.55rem] tracking-[0.3em] uppercase text-pkcharcoal/50 font-semibold">
                    Vezseny
                  </div>
                  <div className="font-serif italic text-pkcharcoal text-base leading-none mt-1">
                    Kis kukták
                  </div>
                </div>
              </div>

              {/* Polaroid overlap */}
              <div className="hidden sm:block absolute -bottom-10 -right-6 w-2/5 aspect-[3/4] overflow-hidden border-[10px] border-white shadow-2xl bg-pkbeige rotate-[3deg] hover:rotate-0 transition-transform duration-700">
                <img
                  src="/images/0uhqu6nu.jpg"
                  alt="Kuglóf"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Vertical ember line */}
              <div className="absolute -left-3 sm:-left-5 top-12 bottom-12 w-px bg-gradient-to-b from-pkred via-pkred/30 to-transparent">
                <span className="absolute -top-1 -left-[3px] w-[7px] h-[7px] rounded-full bg-pkred shadow-[0_0_12px_rgba(217,4,41,0.8)]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mit kínálunk */}
      <section className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="02" />
        {/* Subtle warm glow */}
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-32 w-[460px] h-[460px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #D90429 0%, #FFA64D 55%, transparent 75%)",
          }}
        />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
          <div className="max-w-2xl mb-14 reveal">
            <span className="eyebrow">Kaland</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Mit kínálunk<span className="text-pkred">?</span>
            </h2>
            <p className="mt-6 text-pkcharcoal/70 text-base sm:text-lg italic font-serif">
              Nálunk a főzés nem tanulás, hanem kaland!
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {OFFERS.map(({ text, Icon }, i) => (
              <li
                key={text}
                data-testid={`offer-${i}`}
                className="bg-white border border-pkash p-7 sm:p-8 flex items-start gap-4 reveal group hover:border-pkred/60 hover:shadow-[0_20px_40px_-15px_rgba(217,4,41,0.18)] hover:-translate-y-1 transition-all duration-500"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-full bg-pkbeige flex items-center justify-center shrink-0 group-hover:bg-pkred transition-colors duration-500">
                  <Icon
                    className="w-4 h-4 text-pkred group-hover:text-white transition-colors duration-500"
                    strokeWidth={1.6}
                  />
                </div>
                <div className="flex-1">
                  <div className="text-pkred/40 font-serif text-xs leading-none mb-2 group-hover:text-pkred transition-colors">
                    0{i + 1}
                  </div>
                  <div className="text-pkcharcoal text-base sm:text-lg leading-snug">
                    {text}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why special */}
      <section className="relative bg-white py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="03" />
        <Embers count={10} variant="light" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6 reveal">
            <span className="eyebrow">Lelassít és inspirál</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Miért<br />
              <span className="italic text-pkred">különleges?</span>
            </h2>
          </div>
          <div className="lg:col-span-6 reveal">
            <p className="text-pkcharcoal/75 text-base sm:text-lg leading-relaxed">
              Mert itt nem csak főzni tanulnak a gyerekek. Megtanulják
              értékelni az ételt, türelmesnek lenni, együtt dolgozni, és
              közben észrevétlenül fejlődnek. A természet ritmusa lelassít, a
              vidéki környezet inspirál.
            </p>
          </div>
        </div>
      </section>

      {/* Egy falatnyi gyerekkor */}
      <section className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="04" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6 reveal">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src="/images/0uhqu6nu.jpg"
                alt="Egy falatnyi gyerekkor"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
              />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <span className="eyebrow">Kaland</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Egy falatnyi<br />
              <span className="italic text-pkred">gyerekkor.</span>
            </h2>
            <p className="mt-8 font-serif italic text-pkcharcoal/85 text-xl sm:text-2xl leading-snug font-light">
              „Hiszünk abban, hogy a legszebb emlékek egyszerű pillanatokból
              születnek — lisztes kézből, friss illatokból, állatsimogatásból
              és közös nevetésekből."
            </p>
            <p className="mt-6 text-pkcharcoal/70 text-base sm:text-lg leading-relaxed">
              Nálunk minden adott ahhoz, hogy ezek a pillanatok
              megszülessenek.
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="gyerekkor-cta"
              className="btn-fire mt-10"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              Foglalj hozzánk
            </a>
          </div>
        </div>
      </section>

      {/* Next workshop */}
      <section className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden">
        <SectionNumber n="05" variant="dark" />
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(217,4,41,0.35) 0%, transparent 55%)",
          }}
        />
        <Embers count={14} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <span className="eyebrow center">Következő főzősuli</span>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.08]">
            Hamarosan<br />
            <span className="italic text-pkred">új program érkezik.</span>
          </h2>
          <p className="mt-8 text-white/75 text-lg leading-relaxed">
            Jelenleg a következő gyerek főzősuli időpontján dolgozunk. Kövess
            minket, vagy jelezd érdeklődésed, és elsőként értesítünk, amint
            meghirdetjük az új alkalmat!
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={SITE.phoneHref}
              data-testid="workshop-cta-phone"
              className="btn-fire"
            >
              <Phone className="w-4 h-4" strokeWidth={1.8} />
              Érdeklődöm
            </a>
            <a
              href={SITE.emailHref}
              data-testid="workshop-cta-email"
              className="btn-ghost"
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              Email-en kérdezek
            </a>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <PageGallery
        sectionN="06"
        bg="bg-white"
        eyebrow="Képekben"
        title={
          <>
            Ez vár<br />
            <span className="italic text-pkred">rád.</span>
          </>
        }
        subtitle="Játékos főzés, állatok, lisztes kezek és nevetés — egy kis kuktákkal teli nap a Pakkalandon."
        images={[
          { src: "/images/8k4h10ox.jpg", label: "Tészta bekeverve" },
          { src: "/images/0uhqu6nu.jpg", label: "Kuglóf" },
          { src: "/images/noxn8zib.jpg", label: "Délutáni tea" },
          { src: "/images/bx5lhx0h.jpg", label: "Kis vendégeink" },
          { src: "/images/zhbsgoo7.jpeg", label: "Állatok" },
          { src: "/images/nub9tov3.jpg", label: "Pónik" },
          { src: "/images/d5l3w5vu.jpg", label: "Falatozás" },
          { src: "/images/nkl5175x.jpg", label: "Kemence" },
        ]}
      />

      {/* Upcoming events */}
      <UpcomingEvents sectionN="07" bg="bg-pkbeige" />

      <BookingProcess sectionN="08" />
    </SubpageLayout>
  );
}
